import { useEffect, useRef, useState } from "react";
import { Copy, Loader2, ChevronDown } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

/**
 * Feb-2026 (User-Wunsch UX) — „Vorlage übernehmen"-Dropdown.
 *
 * Lädt die letzten 5 Inserate des angemeldeten Verkäufers und kopiert auf
 * Klick die wichtigsten Stamm- und Ausstattungsdaten in das neue Inserat.
 * Spart Händlern bei Serienangeboten (z. B. mehrere identische Vespa-Roller)
 * massiv Eingabezeit.
 *
 * Felder die NICHT übernommen werden: Bilder, Preis, FIN/HSN/TSN, Kennzeichen,
 * Erstzulassung, Kilometerstand, Vorbesitzer (alles fahrzeugspezifisch).
 */
const COPY_FIELDS = [
    "brand", "model", "manufacturer", "vehicle_type", "category", "condition",
    "color", "color_finish", "color_base", "displacement", "power_kw", "power_hp",
    "fuel", "transmission", "drive_type", "cylinder_count", "seat_count",
    "emission_class", "umweltplakette", "tank_capacity_l", "seat_height_mm",
    "features", "accessories", "service_notes", "warranty_months",
    "electric_battery_type", "quad_allwheel_drive", "quad_lof_zulassung",
    "vat_included", "vat_export_net_calculation", "vat_export_refund_after_docs",
    "location", "street", "postal_code",
];

export default function ListingTemplateDropdown({ setForm, isEdit, fullWidth = false, chipClassName = "" }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const ref = useRef(null);

    useEffect(() => {
        const onDoc = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const load = async () => {
        if (loading) return;
        setLoading(true);
        try {
            // Feb-2026 (User-Wunsch) — primär gespeicherte Vorlagen laden;
            // wenn noch keine vorhanden sind, auf die letzten Inserate zurückfallen.
            const { data: tpls } = await api.get("/listings/templates?limit=5");
            let list = Array.isArray(tpls) ? tpls : (tpls?.items || []);
            if (!list.length) {
                const { data } = await api.get("/listings/mine?limit=5");
                list = Array.isArray(data) ? data : (data?.items || []);
            }
            setItems(list);
        } catch (e) {
            toast.error("Konnte Vorlagen nicht laden");
        } finally {
            setLoading(false);
        }
    };

    const apply = (src) => {
        setForm((p) => {
            const next = { ...p };
            COPY_FIELDS.forEach((k) => {
                if (src[k] != null && src[k] !== "" && !(Array.isArray(src[k]) && src[k].length === 0)) {
                    next[k] = src[k];
                }
            });
            // Titel aus Marke + Modell ableiten (ohne Bj/km, damit der Inserent
            // diese aktuell hält)
            if (next.brand && next.model && (!p.title || p.title.length < 5)) {
                next.title = `${next.brand} ${next.model}`;
            }
            return next;
        });
        toast.success(`Vorlage „${src.brand} ${src.model}" übernommen — Bilder, Preise und Vorbesitzer-Daten bleiben leer.`, { duration: 6000 });
        setOpen(false);
    };

    if (isEdit) return null;

    return (
        <div className={`relative ${fullWidth ? "w-full" : ""}`} ref={ref} data-testid="listing-template-dropdown">
            <button
                type="button"
                onClick={() => { setOpen((o) => !o); load(); }}
                className={chipClassName || `inline-flex items-center ${fullWidth ? "justify-center w-full" : ""} gap-1.5 h-9 px-3 text-xs font-bold uppercase tracking-wider bg-gray-100 hover:bg-gray-200 text-[#111827] border border-gray-300 rounded-sm`}
                data-testid="listing-template-trigger"
            >
                <Copy className="w-3.5 h-3.5" /> Vorlage übernehmen
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="absolute right-0 mt-1.5 w-80 bg-white border-2 border-[#111827] rounded-sm shadow-xl z-40" data-testid="listing-template-menu">
                    <div className="p-3 border-b border-gray-200">
                        <div className="font-bold text-xs uppercase tracking-wider">Letzte 5 Vorlagen</div>
                        <div className="text-[11px] text-gray-600 mt-0.5">Stammdaten, Ausstattung und Standort werden kopiert — Bilder, Preise und Vorbesitzer-Daten bleiben leer.</div>
                    </div>
                    {loading && (
                        <div className="p-6 flex items-center justify-center gap-2 text-sm text-gray-500">
                            <Loader2 className="w-4 h-4 animate-spin" /> Lädt …
                        </div>
                    )}
                    {!loading && items.length === 0 && (
                        <div className="p-4 text-center text-sm text-gray-500">Noch keine Vorlagen vorhanden. Speichere ein Inserat über „Als Vorlage speichern“.</div>
                    )}
                    {!loading && items.length > 0 && (
                        <ul className="max-h-80 overflow-y-auto">
                            {items.map((l) => (
                                <li key={l.id}>
                                    <button
                                        type="button"
                                        onClick={() => apply(l)}
                                        className="w-full text-left px-3 py-2 hover:bg-amber-50 border-b border-gray-100 last:border-b-0"
                                        data-testid={`listing-template-item-${l.id}`}
                                    >
                                        <div className="font-bold text-sm truncate">{l.brand} {l.model}</div>
                                        <div className="text-[11px] text-gray-600 truncate">
                                            {[l.vehicle_type || l.bike_type, l.power_kw ? `${l.power_kw} kW` : null, l.fuel].filter(Boolean).join(" · ") || "Stammdaten-Vorlage"}
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
